import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Toaster } from 'react-hot-toast';
import { Helmet } from 'react-helmet';

// Import stores
import { StoreProvider, useUserStore } from './stores/StoreProvider';

// Import components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Import pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import Dashboard from './pages/Dashboard';
import Games from './pages/Games';
import Drills from './pages/Drills';
import Chat from './pages/Chat';
import Profile from './pages/Profile';
import Friends from './pages/Friends';
import Season from './pages/Season';
import AddGame from './pages/AddGame';
import OnboardingNames from './pages/OnboardingNames';
import OnboardingAvatar from './pages/OnboardingAvatar';
import OnboardingRole from './pages/OnboardingRole';
import OnboardingPosition from './pages/OnboardingPosition';
import OnboardingBirthdayZipcode from './pages/OnboardingBirthdayZipcode';
import OnboardingTeamOrganization from './pages/OnboardingTeamOrganization';
import Organization from './pages/Organization';
import Callback from './pages/Callback';
import HighlightDetail from './pages/HighlightDetail';
import PublicPlayerStats from './pages/PublicPlayerStats';



// App Navigator with Auth Logic
const AppNavigator = observer(() => {
  return (
    <Router>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<Callback />} />
        <Route path="/highlight/:id" element={<HighlightDetail />} />
        <Route path="/player/:userId" element={<PublicPlayerStats />} />
        
        {/* Onboarding routes */}
        <Route path="/onboarding/names" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingNames />
          </ProtectedRoute>
        } />
        <Route path="/onboarding/avatar" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingAvatar />
          </ProtectedRoute>
        } />
        <Route path="/onboarding/role" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingRole />
          </ProtectedRoute>
        } />
        <Route path="/onboarding/position" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingPosition />
          </ProtectedRoute>
        } />
        <Route path="/onboarding/birthday-zipcode" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingBirthdayZipcode />
          </ProtectedRoute>
        } />
        <Route path="/onboarding/team-organization" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <OnboardingTeamOrganization />
          </ProtectedRoute>
        } />

        {/* Protected routes with layout */}
        <Route path="/app" element={
          <ProtectedRoute requireAuth={true} requireProfile={true}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="games" element={<Games />} />
          <Route path="drills" element={<Drills />} />
          <Route path="profile" element={<Profile />} />
          <Route path="friends" element={<Friends />} />
          <Route path="season" element={<Season />} />
          <Route path="add-game" element={<AddGame />} />
          <Route path="organization" element={<Organization />} />
        </Route>

        {/* Chat route - accessible without complete profile */}
        <Route path="/app/chat" element={
          <ProtectedRoute requireAuth={true} requireProfile={false}>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<Chat />} />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
});

// Main App Component
const App = () => {
  return (
    <StoreProvider>
      {/* Default meta tags for the app */}
      <Helmet>
        <title>Biscuit: The Hockey App - Your All-in-One Hockey Community</title>
        <meta name="description" content="Biscuit is your all-in-one hub to follow, celebrate, and improve every hockey journey. Share highlights, track seasons, and connect with the hockey family." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://biscuit.app/" />
        <meta property="og:title" content="Biscuit: The Hockey App - Your All-in-One Hockey Community" />
        <meta property="og:description" content="Share highlights, track seasons, and connect with the hockey family. Built from the ice up for players, parents, and coaches." />
        <meta property="og:image" content="/og.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:site_name" content="Biscuit Hockey" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter - will be overridden by specific pages */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://biscuit.app/" />
        <meta property="twitter:title" content="Biscuit: The Hockey App - Your All-in-One Hockey Community" />
        <meta property="twitter:description" content="Share highlights, track seasons, and connect with the hockey family. Built from the ice up for players, parents, and coaches." />
        <meta property="twitter:image" content="/og.png" />
        <meta property="twitter:creator" content="@biscuithockey" />
        
        {/* Additional Meta Tags */}
        <meta name="keywords" content="hockey, hockey app, hockey community, hockey highlights, hockey tracking, hockey coach, hockey parents, hockey players, hockey season, hockey stats" />
        <meta name="author" content="Biscuit Hockey" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <AppNavigator />
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
    </StoreProvider>
  );
};

export default App; 